'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { useTranslations } from 'next-intl'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import toast from '~/app/_components/toast'
import { useChallenge } from '~/app/sign/_hooks/use_challenge'
import client from '~/lib/client/client'

import type { AuthUserId } from '~/models/auth_user'

export interface Step1Data {
  email: string
  confirm_email: string
  password: string
  confirm: string
  sponsor_username: string
}

interface Step1Props {
  partner: {
    id: AuthUserId
    username: string
  } | null
  onNext: (data: Step1Data) => void
  defaultValues?: Partial<Step1Data>
}

const Step1: React.FC<Step1Props> = ({ partner: p, onNext, defaultValues }) => {
  const t = useTranslations('auth.sign_up.form')
  const [loading, setLoading] = React.useState(false)
  const { challenge } = useChallenge()

  const usernameSchema = z
    .string()
    .min(2, { message: t('username.error.length_min') })
    .max(50, { message: t('username.error.length_max') })
    .regex(/^(?=.*[a-z])[a-z0-9_]+(\.[a-z0-9_]+)*$/, {
      message: t('username.error.format'),
    })

  const schema = z
    .object({
      email: z.string().email({ message: t('email.error') }),
      confirm_email: z.string().email({ message: t('confirm_email.error') }),
      password: z.string().min(6, { message: t('password.error') }),
      confirm: z.string().min(6, { message: t('confirm_password.error') }),
      sponsor_username: usernameSchema,
    })
    .refine((data) => data.password === data.confirm, {
      message: t('confirm_password.match_error'),
      path: ['confirm'],
    })
    .refine((data) => data.email === data.confirm_email, {
      message: t('confirm_email.match_error'),
      path: ['confirm_email'],
    })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<Step1Data>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: defaultValues?.email ?? '',
      confirm_email: defaultValues?.confirm_email ?? '',
      password: defaultValues?.password ?? '',
      confirm: defaultValues?.confirm ?? '',
      sponsor_username: defaultValues?.sponsor_username ?? p?.username ?? '',
    },
  })

  const onSubmit = async (data: Step1Data) => {
    setLoading(true)

    const pow = await challenge()
    const [res, err] = await client<{
      emailExists: boolean
      emailBlocked: boolean
      sponsorExists: boolean
    }>({
      path: '/api/auth/check',
      method: 'POST',
      body: {
        email: data.email,
        sponsor_username: data.sponsor_username,
        ...pow,
      },
    })

    setLoading(false)

    if (err) {
      console.error('Error checking registration data:', err)
      toast({
        type: 'error',
        message: t('errors.default'),
      })
      return
    }

    if (res?.emailExists) {
      setError('email', { message: t('errors.email_exists') })
      return
    }

    if (res?.emailBlocked) {
      setError('email', { message: t('errors.email_blocked') })
      return
    }

    if (!res?.sponsorExists) {
      setError('sponsor_username', {
        message: t('errors.sponsor_not_found'),
      })
      return
    }

    onNext(data)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container rowSpacing={2} columnSpacing={3}>
        <Grid size={{ xs: 12 }}>
          <TextField
            label={t('email.label')}
            placeholder={t('email.placeholder')}
            sx={{ width: '100%' }}
            {...register('email')}
            error={!!errors.email}
            helperText={errors.email?.message?.toString()}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            label={t('confirm_email.label')}
            placeholder={t('confirm_email.placeholder')}
            sx={{ width: '100%' }}
            {...register('confirm_email')}
            error={!!errors.confirm_email}
            helperText={errors.confirm_email?.message?.toString()}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            label={t('password.label')}
            placeholder={t('password.placeholder')}
            type='password'
            sx={{ width: '100%' }}
            {...register('password')}
            error={!!errors.password}
            helperText={errors.password?.message?.toString()}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            label={t('confirm_password.label')}
            placeholder={t('confirm_password.placeholder')}
            type='password'
            sx={{ width: '100%' }}
            {...register('confirm')}
            error={!!errors.confirm}
            helperText={errors.confirm?.message?.toString()}
          />
        </Grid>

        {p ? (
          <Grid size={{ xs: 12 }}>
            <TextField
              label={t('partner.label')}
              placeholder={t('partner.placeholder')}
              sx={{ width: '100%' }}
              disabled
              value={p.username}
            />
          </Grid>
        ) : (
          <Grid size={{ xs: 12 }}>
            <TextField
              label={t('partner.code.label')}
              placeholder={t('partner.code.placeholder')}
              sx={{ width: '100%' }}
              {...register('sponsor_username')}
              error={!!errors.sponsor_username}
              helperText={
                errors.sponsor_username?.message?.toString() ||
                t('partner.code.help')
              }
            />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Button
            variant='contained'
            fullWidth
            type='submit'
            disabled={loading}
            sx={{
              mt: 2,
              py: 1,
              background: 'linear-gradient(135deg, #27B1C6 0%, #59CDA1 100%)',
              boxShadow: '0 2px 8px rgba(39, 177, 198, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2ed3ed 0%, #6df6c1 100%)',
                boxShadow: '0 4px 12px rgba(39, 177, 198, 0.4)',
              },
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            {t('next')}
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}

export default Step1
